import { DashFeaturesEnum } from "dash-common";
import {
  Button,
  FilterContext,
  Flex,
  ListWithPagination,
  ModalContext,
  PaginationContext,
  SearchContext,
  ViewerContext,
  toast,
  useCheckIfSubscriptionFeatureActiveBroker,
  useServerResponseTranslation,
} from "dash-ui";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import { RoutesPath } from "flow-components/routes/routes";

import PropertyCard from "containers/properties/PropertyCard/PropertyCard";
import {
  useGetTrackedPropertiesQuery,
  useGetTrackedPropertiesCountQuery,
  useRemovePropertyFromTrackedMutation,
} from "gql/generated.graphql";
import { GTMEventsEnum } from "common/google-tag-manager/constants";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import {
  buildAddress,
  buildBuiltRange,
  buildRangeString,
} from "common/helpers";

export const StyledList = styled(ListWithPagination)`
  & > div:first-child {
    padding: 0;
    border: none;
  }
  & > div:last-child {
    justify-content: center;
  }
`;

const TrackingPropertiesList = () => {
  const { account, broker } = useContext(ViewerContext);
  const navigation = useNavigate();
  const { t } = useTranslation(["common", "pages"]);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const {
    state: paginationState,
    getRefetchProps,
    setTotal,
  } = useContext(PaginationContext);
  const { state: filterState } = useContext(FilterContext);
  const { searchSlug } = useContext(SearchContext);
  const pagination = getRefetchProps(paginationState);
  const translateServerError = useServerResponseTranslation();
  const { sort = {}, ...filter } = filterState;
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);

  const [removeTrackedProperty] = useRemovePropertyFromTrackedMutation();
  const { data, loading, refetch } = useGetTrackedPropertiesQuery({
    variables: {
      pagination,
      sort:
        (sort.sortOptions || []).length > 0
          ? sort.sortOptions
          : [{ fieldName: "createdAt", sortOrder: -1 }],
      filter: {
        ...filter,
        searchByName: searchSlug,
      },
    },
    fetchPolicy: "network-only",
    skip: !broker,
  });

  const { collection, totalCount } = useMemo(
    () => ({
      collection: data?.getTrackedProperties.collection || [],
      totalCount: data?.getTrackedProperties.totalCount || 0,
    }),
    [data]
  );

  const { data: dataCount, loading: loadingCount } =
    useGetTrackedPropertiesCountQuery({
      variables: {
        pagination: { limit: 1 },
      },
      fetchPolicy: "network-only",
      skip: !broker,
    });

  const { trackedPropertiesCount } = useMemo(
    () => ({
      trackedPropertiesCount: dataCount?.getTrackedProperties.totalCount || 0,
    }),
    [dataCount]
  );

  useEffect(() => {
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);
  //TODO: Replace Record<string, any> by PropertyModel
  const handleRemoveProperty = (
    property: Record<string, any>,
    brokerTrackedPropertyId: string
  ) => {
    removeTrackedProperty({
      variables: { brokerTrackedPropertyId },
      onCompleted: () => {
        if (account) {
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.PROPERTY_REMOVED_FROM_TRACKING,
              user_id: getGoogleTagManagerUserId(account.email),
              property_name: property.name,
              price_range: buildRangeString(property.priceRange),
              property_type: property.buildingTypes,
              number_of_bedrooms: buildRangeString(
                property.averageBedroomsNumberValue
              ),
              number_of_bathrooms: buildRangeString(
                property.averageBathroomsNumberValue
              ),
              number_of_units: property.numberOfUnits,
              appeared_date: property.createdAt.split("T")[0],
              features_and_amenities: property.amenityNames
                ?.map(({ name }: { name: string }) => name.trim())
                .join(", "),
              completion_year: property.yearBuilt,
              project_status: property.projectStatus,
              year_built: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate || `${property.yearBuilt}`,
                yearOnly: true,
              }),
              scheduled_construction: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate || `${property.yearBuilt}`,
              }),
              marketArea: property.marketArea,
              address: buildAddress(property.location.address),
            },
          });
        }
        refetch();
        toast.success(t("pages:propertyPage.propertyRemovedFromTrack"));
      },
      onError: (error) => {
        toast.error(translateServerError(error));
      },
    });
  };

  const lableToShow = !loading
    ? searchSlug
      ? t`common:placeholders.noSearchResults`
      : t`pages:propertyPage.noProperties`
    : "";

  return (
    <StyledList
      mt={6}
      paginationProps={{
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
      listProps={{
        w: "100%",
        loading: loading || loadingCount,
        placeholderProps: trackedPropertiesCount
          ? {
              label: lableToShow,
            }
          : {
              title: loading
                ? ""
                : t("pages:trackingPage:noPropertiesLabelTitle") || "",
              label: loading
                ? ""
                : t("pages:trackingPage:noPropertiesLabel") || "",
              buttonProps: !loading
                ? {
                    onClick: () => navigation(RoutesPath.Properties),
                    children: t("pages:trackingPage:noPropertiesButtonLabel"),
                  }
                : undefined,
            },
        loadingProps: { bgColor: "inherit" },
        grid: [343, 251, 356, 356],
        data: collection,
        gap: 16,
        mb: 2,
      }}
    >
      {({ property, _id: trackedPropertyId }) => (
        <PropertyCard
          property={property}
          renderOptions={() => (
            <Flex>
              <Button
                square
                mr={2}
                icon="folder"
                onClick={(e) => {
                  e.stopPropagation();
                  if (checkFeatureAccess()) {
                    setPayload({ propertyId: property._id });
                    toggleModal(true, "AddPropertyToPortfolio");
                  }
                }}
              />
              <Button
                square
                icon="trash"
                iconProps={{ size: 20 }}
                colorVariation="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveProperty(property, trackedPropertyId);
                }}
              />
            </Flex>
          )}
        />
      )}
    </StyledList>
  );
};

export default TrackingPropertiesList;
